import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { memo, useState } from "react";
import PropTypes from "prop-types";

import { toJalaliFullDate } from "../../../../../middleware/middleware";
import RequestConfirmChoiceGroup from "../../../../../components/request/RequestConfirmChoiceGroup";
import ReviewDecisionActions from "./ReviewDecisionActions";
import { useReviewDecisionActions } from "./useReviewDecisionActions";
import {
  CRM_CARD_BODY_SX,
  CRM_CARD_HEADER_SX,
  CRM_CARD_SX,
  CRM_SECTION_TITLE_SX,
} from "./initialReviewStyles";
import { OPERATOR_DECISION_CARD_SX } from "./operatorReviewStyles";

const StatusField = ({ label, value }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25, lineHeight: 1.6 }}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.5 }}>
      {value || "—"}
    </Typography>
  </Box>
);

StatusField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const ReviewActionsPanel = memo(
  ({
    values,
    formData,
    isEditable,
    fieldDisabled,
    isSubmitting,
    loading,
    onMakeFormikVariable,
    handleChange,
    setFieldValue,
    handleSubmit,
    assignmentOwnerLabel,
    assignedAt,
    workflowStageTitle,
    requestConfirmList,
    onClaim,
    onRelease,
    canClaim,
    canRelease,
    assignmentLoading,
    hideReferAction = false,
    hideReviseAction = false,
    dataOnlyEditMode = false,
    onQuickStatusSubmit = null,
    compact = false,
  }) => {
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const confirmList = requestConfirmList || formData?.requestConfirm || [];

    const decisionActions = useReviewDecisionActions({
      formData: { requestConfirm: confirmList },
      fieldDisabled,
      setFieldValue,
      handleSubmit,
      hideReferAction,
      hideReviseAction,
      onQuickStatusSubmit,
      isBusy: Boolean(loading || isSubmitting),
    });

    return (
      <Paper
        elevation={0}
        sx={{
          ...(compact ? OPERATOR_DECISION_CARD_SX : CRM_CARD_SX),
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box sx={CRM_CARD_HEADER_SX}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GavelOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography sx={CRM_SECTION_TITLE_SX}>تصمیم و یادداشت کارشناس</Typography>
          </Box>
        </Box>

        <Stack spacing={2.5} sx={CRM_CARD_BODY_SX}>
          {!compact ? (
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <StatusField
                  label="وضعیت فعلی"
                  value={values.requestConfirmTitle || "بررسی نشده"}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatusField
                  label="مرحله جاری"
                  value={workflowStageTitle || values.stepTitle || "بررسی اولیه"}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatusField
                  label="اولویت پیگیری"
                  value={values.leadPriorityTitle || "—"}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.25, lineHeight: 1.6 }}
                  >
                    مسئول پیگیری
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    <Chip
                      size="medium"
                      label={assignmentOwnerLabel || "بدون مسئول"}
                      sx={{ fontWeight: 600 }}
                    />
                    {assignedAt ? (
                      <Typography variant="body2" color="text.secondary">
                        {toJalaliFullDate(assignedAt)}
                      </Typography>
                    ) : null}
                  </Stack>
                  {(canClaim || canRelease) && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {canClaim ? (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={assignmentLoading}
                          onClick={onClaim}
                        >
                          دریافت
                        </Button>
                      ) : null}
                      {canRelease ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={assignmentLoading}
                          onClick={onRelease}
                        >
                          آزادسازی
                        </Button>
                      ) : null}
                    </Stack>
                  )}
                </Box>
              </Grid>
            </Grid>
          ) : null}

          <TextField
            label="یادداشت کارشناس"
            multiline
            minRows={compact ? 3 : 4}
            placeholder="نتیجه تماس، توضیحات یا دلیل تصمیم..."
            onChange={handleChange}
            sx={{
              "& .MuiInputBase-root": { lineHeight: 1.6, fontSize: "0.9375rem" },
            }}
            {...onMakeFormikVariable("note")}
            size="medium"
          />

          {isEditable && !dataOnlyEditMode && !hideReviseAction ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(values.notifyApplicant)}
                  disabled={fieldDisabled || isSubmitting}
                  onChange={(event) =>
                    setFieldValue("notifyApplicant", event.target.checked)
                  }
                />
              }
              label="ارسال پیامک به متقاضی"
              sx={{ alignItems: "flex-start", m: 0 }}
            />
          ) : null}

          {isEditable && !dataOnlyEditMode && !hideReviseAction ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
              اگر تیک فعال باشد، با «تایید بررسی اولیه» پیامک تایید، با «نیاز به اصلاح»
              پیامک تکمیل اطلاعات و با «نیاز به پیگیری» پیامک هماهنگی برای متقاضی ارسال می‌شود.
              در «نیاز به پیگیری» حتماً یادداشت کارشناس را در توضیحات ثبت کنید. با «ذخیره» فقط
              تغییرات کارشناس ثبت می‌شود بدون تغییر وضعیت.
            </Typography>
          ) : null}

          {isEditable ? (
            dataOnlyEditMode ? (
              <ReviewDecisionActions
                {...decisionActions}
                fieldDisabled={fieldDisabled}
                isSubmitting={isSubmitting}
                loading={loading}
                layout={compact ? "panel" : "row"}
                showApprove={false}
                showReject={false}
                showRefer={false}
                hideReviseAction
                saveLabel="ذخیره اطلاعات"
              />
            ) : (
              <>
                <Box>
                  <Button
                    size="small"
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: advancedOpen ? "rotate(180deg)" : "none",
                          transition: "0.2s",
                        }}
                      />
                    }
                    onClick={() => setAdvancedOpen((v) => !v)}
                    sx={{ mb: 1 }}
                  >
                    انتخاب دقیق وضعیت
                  </Button>
                  <Collapse in={advancedOpen}>
                    <RequestConfirmChoiceGroup
                      label="تغییر وضعیت"
                      list={confirmList}
                      noteValue={values.note}
                      noteLabel="یادداشت"
                      onNoteChange={(text) => setFieldValue("note", text)}
                      onChange={(nextValue) => setFieldValue("requestConfirm", nextValue)}
                      compact
                      {...onMakeFormikVariable("requestConfirm")}
                    />
                  </Collapse>
                </Box>

                <Divider />

                <ReviewDecisionActions
                  {...decisionActions}
                  fieldDisabled={fieldDisabled}
                  isSubmitting={isSubmitting}
                  loading={loading}
                  layout={compact ? "panel" : "row"}
                  notifyApplicant={Boolean(values.notifyApplicant)}
                />
              </>
            )
          ) : (
            <TextField
              label="وضعیت درخواست"
              value={values.requestConfirmTitle || "—"}
              disabled
              fullWidth
              size="medium"
              variant="outlined"
            />
          )}
        </Stack>
      </Paper>
    );
  }
);

ReviewActionsPanel.displayName = "ReviewActionsPanel";

ReviewActionsPanel.propTypes = {
  values: PropTypes.object.isRequired,
  formData: PropTypes.object,
  isEditable: PropTypes.bool,
  fieldDisabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  loading: PropTypes.bool,
  onMakeFormikVariable: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  assignmentOwnerLabel: PropTypes.string,
  assignedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  workflowStageTitle: PropTypes.string,
  requestConfirmList: PropTypes.array,
  onClaim: PropTypes.func,
  onRelease: PropTypes.func,
  canClaim: PropTypes.bool,
  canRelease: PropTypes.bool,
  assignmentLoading: PropTypes.bool,
  hideReferAction: PropTypes.bool,
  hideReviseAction: PropTypes.bool,
  dataOnlyEditMode: PropTypes.bool,
  onQuickStatusSubmit: PropTypes.func,
  compact: PropTypes.bool,
};

export default ReviewActionsPanel;
